import type { FC } from 'react'
import { Suspense, useContext, useMemo, useRef, useState } from 'react'
import FluentOpen20Filled from '~icons/fluent/open-20-filled'
import {
  CommentComposer,
  CommentList,
  CommentProvider,
} from 'src/components/ItemComment'
import { CommentsScrollContext } from 'src/components/ItemComment/commentsScroll'
import { UILink } from 'src/components/ui/Link'
import Text from 'src/components/ui/Text'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { EmailVerifyBanner } from 'src/features/User/ForceVerifyEmail/EmailVerifyBanner'
import { useGetArtworkWithTaskDetail } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import { RefType } from 'src/services/apollo/generated/graphql'
import { getArtworkLink } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { dynamic } from 'src/utils/ssr'
import { track } from 'src/utils/tracker'
import ArtworkActionButton from '../ArtworkActions/ArtworkActionButton'
import type { ArtworkDetailContextType, ArtworkDetailProps } from '../common'
import { ArtworkDetailContext } from '../common'
import {
  ArtworkDescriptionAndTags,
  ArtworkDetailAuthorArtworks,
  ArtworkDetailAuthorInfo,
  ArtworkDetailTitle,
} from './ArtworkDetailAuthorInfo'
import ArtworkDetailBottomSectionsClient from './ArtworkDetailBottomSectionsClient'
import { ArtworkDetailCommentAreaRedesign } from './ArtworkDetailCommentAreaRedesign'
import ArtworkDetailContent from './ArtworkDetailContent'
import { ArtworkDeriveButtons } from './ArtworkDetailDeriveButtons'
import ArtworkDetailImage from './ArtworkDetailImage'
import styles from './index.module.css'
import { parseDisplayableParametersFromTask } from './parser'
import { useNarrowScreenForArtworkDetail } from './utils'

const ClientArtworkDetailBottomSections = dynamic(
  () => import('./ArtworkDetailBottomSectionsClient'),
  { ssr: false },
)

const ArtworkDetail: FC<ArtworkDetailProps> = ({
  artwork: artworkFromProps,
  artworkLoras: artworkLorasFromProps,
  taskParameters: taskParametersFromProps,

  isSinglePage,
  shouldExploreStyleTargetBlank,
  onClose,
  onLikeClickForRec,
  trackingArgs,
}) => {
  const { data: artworkFromData, refetch: refetchArtwork } =
    useGetArtworkWithTaskDetail({
      id: artworkFromProps.id,
    })
  const { data: userInfo } = useMyInfo()

  const taskParameters =
    artworkFromData?.taskParameters || taskParametersFromProps
  const artworkLoras = artworkFromData?.loras || artworkLorasFromProps
  // Should omit (loras, taskParameters), but we're keeping them here for simplicity
  const artwork = artworkFromData || artworkFromProps

  const userIsAuthor = userInfo != null && userInfo?.id === artwork?.author?.id

  const parsedTaskParameters = useMemo(
    () => parseDisplayableParametersFromTask(taskParameters, artwork),
    [taskParameters, artwork],
  )

  const ctxValue = useMemo(() => {
    return {
      artwork,
      artworkLoras,
      taskParameters,
      parsedTaskParameters,
      isSinglePage,
      shouldExploreStyleTargetBlank,
      onClose,
      onLikeClickForRec,
      userIsAuthor,
      refetchArtwork,
      trackingArgs,
    } satisfies ArtworkDetailContextType
  }, [
    artwork,
    artworkLoras,
    taskParameters,
    parsedTaskParameters,
    isSinglePage,
    shouldExploreStyleTargetBlank,
    onClose,
    onLikeClickForRec,
    userIsAuthor,
    refetchArtwork,
    trackingArgs,
  ])

  const narrowScreen = useNarrowScreenForArtworkDetail(isSinglePage)

  const actions = (
    <div
      className="absolute top-2 right-2 flex gap-x-2 gap-y-2 flex-wrap"
      onClick={e => e.stopPropagation()}
    >
      {!isSinglePage && (
        <UILink
          href={getArtworkLink(artwork)}
          target="_blank"
          icon={<FluentOpen20Filled className="size-5" />}
          className={cx(
            'rounded-md flex items-center justify-center size-8 transition duration-100',
            'text-white/50 bg-black/50 ',
            'hover:text-white data-[pressed]:text-white hover:bg-black/80 data-[pressed]:bg-black/80',
          )}
        />
      )}

      <ArtworkActionButton
        className={cx(
          'ml-auto size-8 transition duration-100',
          'text-white/50 bg-black/50 ',
          'hover:text-white data-[pressed]:text-white hover:bg-black/80 data-[pressed]:bg-black/80',
        )}
        artwork={artwork}
        taskParameters={taskParameters}
      />
    </div>
  )
  const sectionRef = useRef<HTMLElement>(null)
  const commentRefs = useMemo(() => new Map<string, HTMLElement>(), [])
  const commentsScrollValue = useMemo(
    () => ({ sectionRef, commentRefs }),
    [sectionRef, commentRefs],
  )
  if (!artwork) return null

  return (
    <ArtworkDetailContext.Provider value={ctxValue}>
      <CommentsScrollContext.Provider value={commentsScrollValue}>
        <main
          className={cx(
            'bg-background w-full rounded-xl',
            styles['artwork-main-wrapper'],
            isSinglePage && styles['single-page'],
          )}
        >
          <div className={cx(styles['artwork-detail-content-container'])}>
            <ArtworkDetailImage artwork={artwork} actions={actions} />
            <ArtworkDetailTitle
              artwork={artwork}
              isSinglePage={isSinglePage}
              className="py-4 min-w-0"
            />
            <ArtworkDescriptionAndTags artwork={artwork} />

            {narrowScreen ? (
              <ArtworkDeriveButtons
                artwork={artwork}
                parsedTaskParameters={parsedTaskParameters}
                taskParameters={taskParameters}
                isSinglePage={isSinglePage}
                className="mt-4"
              />
            ) : null}
          </div>
          <section className="flex flex-col gap-2 [grid-area:description] p-4 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
            <ArtworkDetailAuthorInfo artwork={artwork} />
            <ArtworkDetailAuthorArtworks
              artwork={artwork}
              isSinglePage={isSinglePage}
            />
          </section>
          <section className="[grid-area:comment] min-w-0">
            <ArtworkDetailCommentAreaSwitcher />
          </section>
          <ArtworkDetailContent
            artwork={artwork}
            artworkLoras={artworkLoras}
            parsedTaskParameters={parsedTaskParameters}
            taskParameters={taskParameters}
            className="[grid-area:detail]"
            isSinglePage={isSinglePage}
          />
        </main>
        {/*
          Recommendation surfaces (`ArtworkRelevantArtworksInline`,
          `ArtworkRecommendedUsers`, `ArtworkRecommendedArtworks`) are
          personalised and never useful to anonymous SSR — they only
          matter once the client has hydrated and the auth header is
          set. Lazy-load them via `dynamic({ ssr: false })` so the
          SSR HTML stays focused on the detail surface and the heavy
          recommendation grids do not block hydration. Bottom-sections
          markup itself lives in `ArtworkDetailBottomSectionsClient`
          (kept in sync with main's inline render here).
        */}
        {isSinglePage && (
          <ClientArtworkDetailBottomSections artworkId={artwork.id} />
        )}
      </CommentsScrollContext.Provider>
    </ArtworkDetailContext.Provider>
  )
}

export default ArtworkDetail

function ArtworkDetailCommentAreaSwitcher() {
  const { isTreatment } = useCommentsRedesignExperiment()
  return isTreatment ? (
    <ArtworkDetailCommentAreaRedesign />
  ) : (
    <ArtworkDetailCommentArea />
  )
}

function ArtworkDetailCommentArea() {
  const { artwork, userIsAuthor, isSinglePage } =
    useContext(ArtworkDetailContext)
  const [toShowMore, setToShowMore] = useState(false)

  if (!artwork) return null

  return (
    <div
      className={cx(
        'w-full bg-neutral-100 dark:bg-neutral-700 rounded-lg p-4',
        !isSinglePage && 'rounded-bl-2xl',
      )}
    >
      <Suspense>
        <CommentProvider
          topicId={artwork.id}
          refType={RefType.Artwork}
          initLoadSize={3}
          canDeleteMessage={() => userIsAuthor}
          onLoadMore={() => {
            // The expansion is not reversible, so we check the previous value
            // to capture the "expand" event.
            setToShowMore(prev => {
              if (!prev) {
                track('show_more_comments')
              }
              return true
            })
          }}
        >
          <div className="flex items-center pb-2">
            <h3 className="font-medium text-base flex items-center">
              <Text i18nKey="artwork:comment" />
              {(artwork.commentCount ?? 0) > 0 && (
                <span className="text-neutral-500 pl-2 text-xs">
                  {artwork.commentCount}
                </span>
              )}
            </h3>
          </div>
          <EmailVerifyBanner className="mb-4" />
          <CommentComposer className="mb-4 pb-4 border-b border-black/20 dark:border-white/20" />
          <CommentList
            windowed={toShowMore}
            loadMode={toShowMore ? 'scroll' : 'button'}
            style={{ height: toShowMore ? 440 : undefined }}
          />
        </CommentProvider>
      </Suspense>
    </div>
  )
}
